import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "../App";
import { toast } from "sonner";
import { Lock } from "lucide-react";

const ChangePasswordPage = ({ isForced = false }) => {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      if (isForced) {
        // For forced password changes, logout and redirect to login
        toast.success("Password changed successfully! Please login with your new password.");
        logout();
        setTimeout(() => {
          navigate('/auth');
        }, 1000);
      } else {
        // For optional password changes, go back to dashboard
        toast.success("Password changed successfully!");
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="brutal-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-primary border-2 border-black flex items-center justify-center">
              <Lock className="w-6 h-6 text-white" strokeWidth={3} />
            </div>
            <h1 className="text-2xl font-syne font-bold">
              {isForced ? "CHANGE PASSWORD REQUIRED" : "CHANGE PASSWORD"}
            </h1>
          </div>

          {isForced && (
            <div className="mb-6 p-4 bg-accent border-2 border-black">
              <p className="font-bold text-sm">⚠️ Security Notice</p>
              <p className="text-sm mt-2">
                For security reasons, you must change your password before accessing your account.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-bold mb-2">Current Password</label>
              <input
                type="password"
                className="brutal-input w-full"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                data-testid="current-password-input"
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-2">New Password</label>
              <input
                type="password"
                className="brutal-input w-full"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                data-testid="new-password-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Minimum 6 characters</p>
            </div>

            <div>
              <label className="block text-sm font-bold mb-2">Confirm New Password</label>
              <input
                type="password"
                className="brutal-input w-full"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                data-testid="confirm-password-input"
              />
            </div>

            <button
              type="submit"
              className="brutal-btn-primary w-full"
              disabled={loading}
              data-testid="change-password-btn"
            >
              {loading ? 'CHANGING...' : 'CHANGE PASSWORD'}
            </button>
          </form>

          {!isForced && (
            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-sm font-bold hover:underline"
                data-testid="back-dashboard-btn"
              >
                BACK TO DASHBOARD
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
